import { FC, useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  SimpleGrid,
  useToast,
  Spinner,
  Center,
  useColorModeValue,
  Heading,
  Divider,
  NumberInput,
  NumberInputField,
  Input,
  Icon,
} from '@chakra-ui/react';
import { AddIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { LeadDTO, deleteLead, getLead, updateLead } from '@/services/leads-service';
import { ANALYST_ROUTES } from '@bemap/utils';
import { PageHeading, Panel } from '@bemap/ui';
import { useTranslation } from 'react-i18next';
import { LeadAction, CreateLeadActionDto, LeadActionsList, CreateActionModal } from '@bemap/crm-microfrontends';
import { getLeadActions, createLeadAction, updateLeadAction } from '@/services/crm-service';
import { HiOutlineMail, HiOutlinePhone } from 'react-icons/hi';

const ViewLeadPage: FC = () => {
  const { leadId } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState<LeadDTO | null>(null);
  const [actions, setActions] = useState<LeadAction[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [completingActionId, setCompletingActionId] = useState<number | null>(null);
  const [dealValue, setDealValue] = useState<string>('');
  const [expectedClose, setExpectedClose] = useState<string>('');
  const [savingDealInfo, setSavingDealInfo] = useState(false);

  const formatDateTime = (value?: string | null) =>
    value ? new Date(value).toLocaleString() : undefined;

  const toDateTimeLocalInput = (value?: string | null) =>
    value ? new Date(value).toISOString().slice(0, 16) : '';

  const loadLead = async () => {
      try {
        setLoading(true);
      const res = await getLead(Number.parseInt(leadId || '0', 10));
      const data = (res as any).message as LeadDTO;
        setLead(data);
      setDealValue(data?.deal_value ?? '');
      setExpectedClose(toDateTimeLocalInput(data?.expected_close_at));
      } catch (e) {
      toast({ status: 'error', title: t('crm.lead_not_found') || 'Lead not found' });
      navigate(ANALYST_ROUTES.LEADS);
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadLead();
  }, [leadId]);

const loadLeadActions = async (leadIdentifier: number) => {
  try {
    setActionsLoading(true);
    const response = await getLeadActions(leadIdentifier);
    const records = ((response as any).message || []) as LeadAction[];
    setActions(records);
  } catch (e) {
    toast({ status: 'error', title: t('crm.actions_load_error') || 'Unable to load actions' });
  } finally {
    setActionsLoading(false);
  }
};

useEffect(() => {
  if (lead?.id) {
    loadLeadActions(lead.id);
  } else {
    setActions([]);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [lead?.id]);

const handleCreateAction = async (data: CreateLeadActionDto) => {
    if (!lead) return;
    try {
    await createLeadAction(lead.id, data);
    toast({ status: 'success', title: t('crm.action_created') || 'Action created' });
    await loadLeadActions(lead.id);
  } catch (error) {
    toast({ status: 'error', title: t('general.error') || 'Error' });
    throw error;
  }
};

const handleCompleteAction = async (actionId: number) => {
  if (!lead) return;
  setCompletingActionId(actionId);
  try {
    await updateLeadAction(lead.id, actionId, { completed: true });
    toast({
      status: 'success',
      title: t('crm.action_completed_success') || 'Action marked as completed',
    });
    await loadLeadActions(lead.id);
  } catch (error) {
    toast({ status: 'error', title: t('general.error') || 'Error' });
  } finally {
    setCompletingActionId(null);
  }
};

const handleSaveDealInfo = async () => {
  if (!lead) return;
  setSavingDealInfo(true);
  try {
    const res = await updateLead(lead.id, {
      deal_value: dealValue !== '' ? dealValue : null,
      expected_close_at: expectedClose ? new Date(expectedClose).toISOString() : null,
    });
    const updated = (res as any).message as LeadDTO;
      setLead(updated);
    setDealValue(updated?.deal_value ?? '');
    setExpectedClose(toDateTimeLocalInput(updated?.expected_close_at));
    toast({ status: 'success', title: t('crm.deal_info_saved') || 'Deal information saved' });
  } catch (error) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    } finally {
    setSavingDealInfo(false);
    }
  };

  const onDelete = async () => {
    if (!lead) return;
    try {
      await deleteLead(lead.id);
      toast({ status: 'success', title: t('general.deleted') || 'Deleted' });
      navigate(ANALYST_ROUTES.LEADS);
    } catch (e) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  if (loading) {
    return (
      <Center h="300px">
        <Spinner size="lg" />
      </Center>
    );
  }

  if (!lead) {
    return null;
  }

  const headingActions = [
    {
      label: t('crm.back_to_crm') || 'Open CRM',
      variant: 'outline' as const,
      onClick: () => navigate(ANALYST_ROUTES.CRM),
    },
    {
      label: t('actions.delete') || 'Delete',
      colorScheme: 'red' as const,
      onClick: onDelete,
    },
  ];

  const emailLabelBg = useColorModeValue('blue.50', 'whiteAlpha.200');
  const emailLabelColor = useColorModeValue('blue.700', 'blue.200');
  const detailLabelColor = useColorModeValue('gray.500', 'gray.400');

  const formattedPhone = lead.phone && lead.phone.trim().length > 0 ? lead.phone : null;

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading title={t('crm.lead_details') || 'Lead Details'} actions={headingActions} />
      <Panel>
        <VStack spacing={4} align="center" p={6}>
          <Heading fontSize={{ base: '2xl', md: '3xl' }} textAlign="center">
            {lead.name && lead.name.trim().length > 0
              ? lead.name
              : t('crm.unnamed_lead') || 'Unnamed lead'}
          </Heading>
          <VStack spacing={2} align="center">
            <HStack spacing={3} align="center">
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                bg={emailLabelBg}
                color={emailLabelColor}
                fontWeight="semibold"
              >
                {t('general.email') || 'Email'}
              </Badge>
              <HStack spacing={1}>
                <Icon as={HiOutlineMail} />
                <Text fontWeight="medium">{lead.email}</Text>
              </HStack>
            </HStack>
            {formattedPhone && (
              <HStack spacing={1} color={detailLabelColor}>
                <Icon as={HiOutlinePhone} />
                <Text fontWeight="medium">{formattedPhone}</Text>
              </HStack>
            )}
          </VStack>
          {lead.created_at && (
            <Text fontSize="sm" color={detailLabelColor}>
              {(t('general.created_at') || 'Created at') + ': ' + formatDateTime(lead.created_at)}
            </Text>
          )}
        </VStack>
      </Panel>

      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
        <Panel>
          <VStack align="stretch" spacing={6} p={6}>
            <Box>
              <Text fontSize="sm" color={detailLabelColor}>
                {t('crm.deal_value') || 'Deal value'}
              </Text>
              <NumberInput
                value={dealValue}
                onChange={(value) => setDealValue(value)}
                min={0}
                precision={2}
                mt={2}
              >
                <NumberInputField placeholder="0.00" />
              </NumberInput>
            </Box>
            <Box>
              <Text fontSize="sm" color={detailLabelColor}>
                {t('crm.expected_close') || 'Expected close'}
              </Text>
              <Input
                mt={2}
                type="datetime-local"
                value={expectedClose}
                onChange={(e) => setExpectedClose(e.target.value)}
              />
            </Box>
            <Box>
              <Text fontSize="sm" color={detailLabelColor}>
                {t('crm.campaign') || 'Campaign'}
              </Text>
              <Badge
                mt={2}
                colorScheme="purple"
                variant="subtle"
                borderRadius="md"
                px={3}
                py={1}
                fontWeight="semibold"
              >
                {lead.campaign_link_id || '—'}
              </Badge>
            </Box>
            <Button
              alignSelf="flex-start"
              colorScheme="blue"
              onClick={handleSaveDealInfo}
              isLoading={savingDealInfo}
            >
              {t('actions.save') || 'Save'}
            </Button>
          </VStack>
        </Panel>

        <Panel>
          <VStack align="stretch" spacing={6} p={6}>
            <Box>
              <HStack justify="space-between">
                <Text fontWeight="semibold">{t('crm.assessment') || 'Assessment'}</Text>
                {lead.assessment_id && (
                  <Button
                    as={RouterLink}
                    size="sm"
                    rightIcon={<ExternalLinkIcon />}
                    to={ANALYST_ROUTES.ASSESSMENTS_VIEW.replace(
                      ':id',
                      lead.assessment_id.toString()
                    )}
                    colorScheme="blue"
                    variant="solid"
                  >
                    {t('crm.open_assessment') || 'View assessment'}
                  </Button>
                )}
              </HStack>
              <Text fontSize="sm" color={useColorModeValue('gray.500', 'gray.400')} mt={2}>
                {lead.assessment_id
                  ? t('crm.assessment_available') || 'Assessment linked to this lead.'
                  : t('crm.assessment_empty') || 'No assessments linked to this lead yet.'}
              </Text>
            </Box>

            <Divider />

            <Box>
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="semibold">{t('crm.actions') || 'Actions'}</Text>
                <Button
                  size="sm"
                  colorScheme="blue"
                  leftIcon={<AddIcon />}
                  onClick={() => setIsActionModalOpen(true)}
                  isDisabled={actionsLoading}
                >
                  {t('crm.add_action') || 'Add action'}
                </Button>
              </HStack>
              <LeadActionsList
                actions={actions}
                loading={actionsLoading && actions.length === 0}
                onComplete={handleCompleteAction}
                emptyText={t('crm.no_actions') || 'No actions scheduled for this lead yet.'}
                labels={{
                  overdue: t('crm.overdue') || 'Overdue',
                  upcoming: t('crm.upcoming') || 'Upcoming',
                  completeAction: t('crm.complete_action') || 'Mark as completed',
                }}
                completingActionId={completingActionId}
              />
            </Box>
          </VStack>
        </Panel>
      </SimpleGrid>
      <CreateActionModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        onSubmit={handleCreateAction}
      />
    </VStack>
  );
};

export default ViewLeadPage;


